import React, { useCallback, useState } from 'react';
import { Button, Filter, Page, Typo, Flex, Loading } from '../../ui';
import { useNotificationsByType } from './hooks';
import { useNotificationsActions } from './hooks';
import { notificationFilterOptions } from './hooks/constants';
import styled from 'styled-components';
import NotificationItem from './NotificationItem';
import { useTranslation, Trans } from 'react-i18next';
import PageControls from '../../ui/Page/PageControls';
import PageHeader from '../../ui/Page/PageHeader';
import { useRefetchOnAuthChange } from '../auth/hooks';
import PageSection from '../../ui/Page/PageSection';

const NotificationsList = styled.ul`
  margin: 0 0 2rem 0;
  width: 100%;
  padding: 0;
`;

const Notifications = () => {
  const { t } = useTranslation();

  const [length, setLength] = useState(25);
  const [notificationType, setNotificationType] = useState('0');
  const [searchQuery, setSearchQuery] = useState('');
  const [isArchiving, setIsArchiving] = useState(false);

  const { items, isLoading, isError, refetch } = useNotificationsByType(notificationType, {
    search: searchQuery,
    reversed: false,
    pageLength: length,
  });

  useRefetchOnAuthChange(refetch);

  const { markNotification } = useNotificationsActions();

  const handleSelected = useCallback(
    async (item) => {
      await markNotification({
        method: item.isRead === 0 ? 'markRead' : 'markUnread',
        ids: [item.id],
      });
    },
    [markNotification],
  );

  const archiveNotifications = useCallback(
    async (method) => {
      setIsArchiving(true);
      try {
        await markNotification({ method, markAll: true });
      } catch (error) {
        // Error handling is now in the actions hook
      } finally {
        setIsArchiving(false);
      }
    },
    [markNotification],
  );

  const handleNotificationType = useCallback((value) => {
    setNotificationType(value);
  }, []);

  const loadMore = useCallback(() => {
    setLength((prevLength) => prevLength + 25);
  }, []);

  const handleSearch = useCallback((value) => {
    setSearchQuery(value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  return (
    <Page
      staticElements={
        <PageHeader>
          <PageControls
            onClearSearch={clearSearch}
            onSearch={handleSearch}
            placeholder={t('globals.search')}
            disableClear={!searchQuery}
            openModelButton={
              notificationType === '0' ? (
                <Button icon='Archive' disabled={isArchiving} aria-busy={isArchiving} onClick={() => archiveNotifications('markRead')}>
                  {isArchiving ? t('globals.working') : t('notifications.archiveAll')}
                </Button>
              ) : (
                notificationType === '4' && (
                  <Button icon='Archive' disabled={isArchiving} aria-busy={isArchiving} onClick={() => archiveNotifications('markUnread')}>
                    {isArchiving ? t('globals.working') : t('notifications.unarchiveAll')}
                  </Button>
                )
              )
            }
          >
            <Filter info={notificationFilterOptions} handleFilter={handleNotificationType} selected={notificationType} />
          </PageControls>
        </PageHeader>
      }
    >
      <PageSection>
        <Flex width='100%' direction='column' align='flex-start' gap='20'>
          <Flex direction='column' align='flex-start' gap='5'>
            <Typo.Info>{t('notifications.archiveForNoEmail')}</Typo.Info>
            <Typo.Info>
              <Trans t={t} i18nKey={'notifications.switch'} components={[<a href='/profile/notifications'>{t('globals.notifications')}</a>]} />
            </Typo.Info>
          </Flex>

          {isError && (
            <div role='alert'>
              <Typo.ContentSmallHeader>{t('globals.error')}</Typo.ContentSmallHeader>
              <Button onClick={refetch}>{t('globals.retry')}</Button>
            </div>
          )}

          {(isLoading || isArchiving) && <Loading />}

          {items?.length ? (
            <NotificationsList>
              {items.map((item) => (
                <NotificationItem key={item.id} info={item} selectClick={() => handleSelected(item)} />
              ))}
            </NotificationsList>
          ) : (
            !isLoading &&
            !isArchiving && (
              <Flex direction='column' align='center' gap='10'>
                <Typo.ContentSmallHeader>{t('notifications.noMatch')}</Typo.ContentSmallHeader>
                {!!searchQuery && <Button onClick={clearSearch}>{t('globals.clearSearch')}</Button>}
              </Flex>
            )
          )}

          {items?.length >= 25 && <Button onClick={loadMore}>{t('notifications.loadMore')}</Button>}
        </Flex>
      </PageSection>
    </Page>
  );
};

export default Notifications;
